import type { NarrativeClause, RuleBook } from "../logic/narrative";

// The Rule Book: the narrative view of a report configuration, meant to be read start to
// finish by someone who did not build it. Everything it shows comes from
// logic/narrative.ts - this component adds no facts of its own, it only lays the document
// out. Nothing here collapses by default: a rule book that has to be clicked open section
// by section is a form, not a document.
//
// The structured views (RuleSummaryPreview, "Summary" and "Rule Details") are unchanged and
// still there. They answer a different question - "is this configured the way I meant?" -
// and the token rows are better at that than prose is.

interface RuleBookPreviewProps {
    book: RuleBook | null;
}

export function RuleBookPreview({ book }: RuleBookPreviewProps) {
    if (!book) {
        return (
            <div className="rc-rule-book">
                <p className="rc-summary-muted">Choose a report to read its rule book.</p>
            </div>
        );
    }

    return (
        <article className="rc-rule-book">
            <header className="rc-rule-book-head">
                <h3>{book.title}</h3>
                {!!book.subtitle && <p>{book.subtitle}</p>}
            </header>
            {book.sections.map((section) => (
                <section className="rc-rule-book-section" key={section.id}>
                    <h4>{section.heading}</h4>
                    {!!section.lead && <p className="rc-rule-book-lead">{section.lead}</p>}
                    {section.paragraphs.map((paragraph, index) => <p key={`${section.id}-p${index}`}>{paragraph}</p>)}
                    {section.blocks.map((block) => (
                        <div className="rc-rule-book-block" key={block.id}>
                            <h5>
                                {block.title}
                                {!!block.subtitle && <small>{block.subtitle}</small>}
                            </h5>
                            {block.paragraphs.map((paragraph, index) => <p key={`${block.id}-p${index}`}>{paragraph}</p>)}
                            {!!block.outline && (
                                <>
                                    <p className="rc-rule-book-outline-intro">Written out in full:</p>
                                    <ul className="rc-rule-book-clause-list root">
                                        <ClauseOutline clause={block.outline} />
                                    </ul>
                                </>
                            )}
                        </div>
                    ))}
                </section>
            ))}
        </article>
    );
}

/**
 * A deeply nested condition as an indented list.
 *
 * A two-level condition reads fine as one sentence and the narrative layer keeps it that
 * way; past that, the parentheses start doing work the reader has to undo, so the same
 * clause tree is laid out instead. The "all"/"any" wording is the group's own join
 * operator, which is where the query's precedence lives - not a restatement of it.
 */
function ClauseOutline({ clause, depth = 0 }: { clause: NarrativeClause; depth?: number }) {
    if (clause.kind === "condition") return <li className="rc-rule-book-clause">{clause.text}</li>;

    return (
        <li className="rc-rule-book-clause group">
            <span className="rc-rule-book-clause-join">{clause.join === "or" ? "Any of the following" : "All of the following"}</span>
            <ul className="rc-rule-book-clause-list">
                {clause.clauses.map((child, index) => <ClauseOutline clause={child} depth={depth + 1} key={`${depth}-${index}`} />)}
            </ul>
        </li>
    );
}
